import { Injectable } from '@angular/core';
import { GenesisUnitOfWork } from 'app/core/data/genesis-unit-of-work';
import { Part, OrderLine, Order, OrderLineDetailNote, OrderLineDetail, ShipAddress } from 'app/core/model/entity-model';
import moment from 'moment';
import { AuthService } from '../auth.service';
import _ from 'lodash';

@Injectable()
export class EntitiesService {
    constructor(private uow: GenesisUnitOfWork, private auth: AuthService) {}

    async createOrderLine(
        partId: number,
        order: any = null,
        quantity: number = 1,
        extendedQuantity: number = 1,
        parentOrderLine: any = null,
        partLinkIdSource: any = null,
        detached: boolean = false,
        isOption: boolean = false,
        skipParent: boolean = false
    ) {
        // retrieve the base part
        const part: any = await this.uow.parts.byId(partId, 'partLinks');
        if (!part) {
            return null;
        } else {
            return this.createOrderLineForPart(part, order, quantity, extendedQuantity, parentOrderLine, partLinkIdSource, detached, isOption, skipParent);
        }
    }

    async createOrderLineForPart(
        part: Part,
        order: Order = null,
        quantity: number = 1,
        extendedQuantity: number = 1,
        parentOrderLine: any = null,
        partLinkIdSource: any = null,
        detached: boolean = false,
        isOption: boolean = false,
        skipParent: boolean = false
    ) {
        let orderLine: any = null;
        if (!part) {
            return null;
        } else if (!skipParent) {
            // for the part, add a order line
            let lineNumber = 1;
            if (!!order) lineNumber = order.getNextLineNumber();
            orderLine = this.uow.orderLines.createEntity();
            if (detached || skipParent) {
                orderLine.entityAspect.setDeleted();
            } else {
                orderLine.part = part;
            }
            orderLine.order = order;
            orderLine.parentLine = parentOrderLine;
            orderLine.quantity = quantity;
            orderLine.extendedQuantity = extendedQuantity;
            orderLine.partNumber = part.partNumber;
            orderLine.description = part.description;
            orderLine.cost = part.cost;
            orderLine.price = part.price;
            orderLine.lineNumber = lineNumber;
            orderLine.partLinkIdSource = partLinkIdSource;
            orderLine.isOption = isOption;
        }

        // get the parts as a batch to save calls to database
        if (part.partLinks.length > 0) {
            const maxIds = 20;
            let ids = part.partLinks.map((partLink) => {
                return partLink.subPartId;
            });
            while (ids.length > 0) {
                let getIds = ids;
                if (ids.length > maxIds) {
                    getIds = ids.slice(0, maxIds);
                    ids = ids.slice(maxIds);
                } else {
                    ids = [];
                }
                await this.uow.parts.byIds(getIds, 'partLinks');
            }

            // for the part links create order line also
            for (let idx = 0; idx < part.partLinks.length; idx++) {
                const partLink = part.partLinks[idx];
                await this.createOrderLineForPart(
                    partLink.subPart,
                    order,
                    partLink.quantity,
                    extendedQuantity * partLink.quantity,
                    skipParent ? parentOrderLine : orderLine,
                    partLink.id,
                    detached
                );
            }
        }

        return orderLine;
    }

    createOrderLineDetailNote(orderLineDetailId: number, note: any, userId: number, delay?: number, manualNote: boolean = false) {
        const orderLineDetailNote = this.uow.orderLineDetailNotes.createEntity() as OrderLineDetailNote;

        orderLineDetailNote.createDate = moment()
            .add(delay ? delay : 0, 'second')
            .toDate();
        orderLineDetailNote.createdById = userId;
        orderLineDetailNote.orderLineDetailId = orderLineDetailId;
        orderLineDetailNote.note = note;
        orderLineDetailNote.manualNote = manualNote;
    }

    addOrderLine(part?: any, quantity?: number, parentLineId?: number, order?: any, mode?: any, description?: any) {
        const line = this.uow.orderLines.createEntity() as OrderLine;
        line.lineNumber = (<Order>order).getNextLineNumber();
        if (part) {
            line.partId = part.id;
            line.partNumber = part.partNumber;
            line.description = !description ? part.description : description;
            line.cost = part.cost;
            line.originalPrice = part.price;
            line.price = !!order.pricingTierId ? part.getTierPrice(order.pricingTierId) : part.price;
            line.priceAch = line.achLineAmount;
            line.note = !!description ? description : null;
        }
        line.order = order;
        line.quantity = quantity;
        line.extendedQuantity = quantity;
        if (parentLineId) {
            line.parentLineId = parentLineId;
            line.isOption = true;
        }
        if (mode === 'Replacements') line.isReplacement = true;
        return line;
    }

    addOrderLineForMisc(orderLine?: any, quantity?: number, order?: any) {
        const line = this.uow.orderLines.createEntity() as OrderLine;
        line.lineNumber = (<Order>order).getNextLineNumber();
        line.partNumber = orderLine.partNumber;
        line.description = orderLine.description;
        line.price = orderLine.price;
        line.order = order;
        line.quantity = quantity;
        line.extendedQuantity = quantity;
        return line;
    }

    addOrderLineDetail(orderLine: any, userId: number, parentLineId?: number, packSlipId?: number, statusId?: number) {
        const detail = this.uow.orderLineDetails.createEntity() as OrderLineDetail;
        detail.statusId = !!statusId ? statusId : OrderLineDetail.Statuses.New;
        detail.orderLineId = orderLine.id;
        detail.parentLineId = parentLineId;
        detail.isOption = orderLine.isOption;
        detail.isReplacement = orderLine.isReplacement;
        detail.isMisc = !orderLine.parentId && !orderLine.partId;
        detail.quantity = orderLine.isReplacement || (!orderLine.partId && !orderLine.isReplacement) ? orderLine.quantity : 1;
        detail.created = new Date();
        detail.vIN = !!orderLine.part ? orderLine.part.vINPrefix : null;
        detail.packingSlipId = packSlipId;
        detail.price = orderLine.price;

        this.createOrderLineDetailNote(detail.id, 'Submitted', userId);
        if (!!orderLine.note) this.createOrderLineDetailNote(detail.id, orderLine.note, userId, 2, true);

        return detail;
    }

    async addNote(selectedRows: any, note: string, markAsManualNote: boolean) {
        for (let index = 0; index < selectedRows.length; index++) {
            const row = selectedRows[index];
            const orderLineDetailNote = this.uow.orderLineDetailNotes.createEntity({
                createDate: new Date(),
                createdById: this.auth.currentUser.id,
                note: note,
                orderLineDetailId: row.id,
                manualNote: markAsManualNote,
            }) as OrderLineDetailNote;
        }
    }

    setNoteHtml(orderLineDetails: any[]) {
        orderLineDetails.forEach((detail: any) => {
            let rowNote = '';
            if (!!detail.notes && detail.notes.length > 0) {
                const orderedNotes = _.orderBy(detail.notes, 'createDate');
                orderedNotes.forEach((note) => {
                    rowNote += `<span${note.manualNote && note.createdById !== this.auth.currentUser.id && !note.preAllocationNote ? ' class="manual-notes"' : ''}
                        ${note.preAllocationNote ? ' class ="preallocation-notes"' : ''}>${
                        note ? `${note.dateDisplay} by ${note.createdBy.userName}: ${note.note}` : ''
                    }</span><br>`;
                });
            }
            detail.noteHTML = `${rowNote}`;
        });
    }

    setOptionHtml(orderLineDetails: any[]) {
        if (!orderLineDetails) return;

        const headers = ['Part #', 'Description', 'Note'];
        orderLineDetails.forEach((detail) => {
            if (!detail.optionDetails || detail.optionDetails.length === 0) return;

            const html = `<table>
                    <thead><tr><th><b>${headers.join('</b></th><th><b>')}</b></th></tr></thead>
                    <tbody>${this.getOptionHtml(detail)}</tbody>
                </table>`;
            detail.optionHTML = html;
        });
    }

    // sets row.shipAddressNotes to the dealer profile notes for the order's ship-to location
    async setShipAddressNotes(rows: any[], shipAddresses?: ShipAddress[]) {
        if (!rows || rows.length === 0) return;

        shipAddresses ??= (await this.uow.shipAddresses.all()) as ShipAddress[];
        const shipAddressMap = new Map(shipAddresses.map((address) => [address.id, address]));
        rows.forEach((row) => {
            const order: Order = row.orderLine?.order;
            if (!order) return;
            row.shipAddressNotes = order.shipAddressId ? shipAddressMap.get(order.shipAddressId)?.notes : order.dealer?.shippingNote;
        });
    }

    private getOptionHtml(detail: any) {
        let html = '';
        if (detail.id === 48611) console.log(detail.optionDetails);
        _(detail.optionDetails)
            .sortBy('orderLine.partNumber')
            .forEach((option) => {
                const color = this.getOptionHtmlColor(detail, option);
                const cells = this.getOptionCellData(option);
                const bold = this.getOptionHtmlBold(detail, option);
                const strikeThru = this.getOptionHtmlStrikeThru(detail, option);
                html += `<tr><td>${bold ? '<b>' : ''}${strikeThru ? '<s>' : ''}<font color='${color}'>${cells.join(
                    `</font>${strikeThru ? '<s>' : ''}${bold ? '</b>' : ''}</td><td>${bold ? '<b>' : ''}${strikeThru ? '<s>' : ''}<font color='${color}'>`
                )}</font>${strikeThru ? '<s>' : ''}${bold ? '</b>' : ''}</td></tr>`;
            });
        return html;
    }

    private getOptionCellData(option: any) {
        return [
            option?.orderLine?.partNumber ?? '',
            option?.orderLine?.description ?? '',
            (option?.orderLine?.partNumber?.toLowerCase().trim() === 'miscellaneous' ? option?.orderLine?.note : '') ?? '',
        ];
    }

    private getOptionHtmlBold(detail: any, option: any) {
        if (option.deleted) return true;
        if (detail.status.countAsWIP) {
            const isAddedOption = moment.duration(moment(option.created).diff(moment(detail.created))).asSeconds() > 60;
            if (isAddedOption) return true;
        }
        return false;
    }

    private getOptionHtmlStrikeThru(detail: any, option: any) {
        if (option.deleted) return true;

        return false;
    }

    private getOptionHtmlColor(detail: any, option: any) {
        if (option.deleted) return 'red';
        else if (detail.status.countAsWIP) {
            const isAddedOption = moment.duration(moment(option.created).diff(moment(detail.created))).asSeconds() > 60;
            if (isAddedOption) return 'green';
        } else if (option.built) return 'blue';
        return '';
    }

    optionsAndNotesCustomizeCellOverride = ({ gridCell, excelCell }) => {
        excelCell.font = { name: 'Arial', size: 12 };
        excelCell.alignment = { horizontal: 'left' };
        if (gridCell.rowType !== 'data' || !gridCell.column) return;
        if (gridCell.column.dataField === 'optionHTML') {
            excelCell.value = this.getOptionsForExport(gridCell.data.optionDetails);
            excelCell.alignment = { wrapText: true };
        }
        if (gridCell.column.dataField === 'noteHTML') {
            excelCell.value = this.getNotesForExport(gridCell.data.notes);
            excelCell.alignment = { wrapText: true };
        }
    };

    private getOptionsForExport(optionDetails: any[]) {
        const lines = [];
        _(optionDetails)
            .filter((option) => !option.deleted)
            .sortBy('orderLine.partNumber')
            .forEach((option) => {
                const cells = this.getOptionCellData(option);
                lines.push(cells.filter((s) => s).join(' --- '));
            });
        return lines.join('\n');
    }

    private getNotesForExport(notes: any[]) {
        const lines = [];
        notes.forEach((note) => {
            lines.push(`${note.dateDisplay} by ${note.createdBy.userName}: ${note.note}`);
        });
        return lines.join('\n');
    }
}
